import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { buildVCard } from "@/lib/vcard";

const GREEN = "#1B4332";

function buildSignatureHtml({ name, email, profile, qrDataUrl }) {
  const p = profile || {};
  const line = (v) => (v ? `<div style="color:#374151;font-size:13px;line-height:20px;">${v}</div>` : "");
  const contact = [
    p.title && `<div style="color:#6b7280;font-size:13px;line-height:20px;">${p.title}${p.department ? " · " + p.department : ""}</div>`,
    `<div style="color:${GREEN};font-weight:700;font-size:13px;line-height:20px;">${p.company || "Renova Eco Systems"}</div>`,
    p.phone && line(`📞 ${p.phone}`),
    line(`✉️ <a href="mailto:${p.work_email || email}" style="color:#374151;text-decoration:none;">${p.work_email || email}</a>`),
    p.website && line(`🌐 <a href="${p.website.startsWith("http") ? p.website : "https://" + p.website}" style="color:${GREEN};text-decoration:none;">${p.website}</a>`),
  ].filter(Boolean).join("");

  const qrCell = qrDataUrl
    ? `<td style="vertical-align:top;padding-left:18px;border-left:2px solid ${GREEN};">
         <img src="${qrDataUrl}" width="86" height="86" alt="Scan to save contact" style="display:block;"/>
         <div style="color:#9ca3af;font-size:10px;text-align:center;margin-top:4px;">Scan to save</div>
       </td>`
    : "";

  return `<table cellpadding="0" cellspacing="0" style="font-family:Arial,Helvetica,sans-serif;">
    <tr>
      <td style="vertical-align:top;padding-right:18px;">
        <div style="font-weight:800;font-size:16px;color:#0a0a0a;line-height:22px;">${name || ""}</div>
        ${contact}
      </td>
      ${qrCell}
    </tr>
  </table>`;
}

export default function EmailSignature({ name, email, profile = {} }) {
  const qrHolder = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const vcard = buildVCard({ name, email, profile });

  useEffect(() => {
    const canvas = qrHolder.current?.querySelector("canvas");
    if (canvas) setQrDataUrl(canvas.toDataURL("image/png"));
  }, [vcard]);

  const html = buildSignatureHtml({ name, email, profile, qrDataUrl });

  const copy = async () => {
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([`${name}\n${profile.title || ""}\n${profile.work_email || email}`], { type: "text/plain" }),
        }),
      ]);
      toast.success("Signature copied — paste into your email settings");
    } catch {
      try {
        await navigator.clipboard.writeText(html);
        toast.success("Signature HTML copied");
      } catch {
        toast.error("Could not copy. Select the preview and copy manually.");
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="email-signature">
      <div className="hidden" ref={qrHolder}>
        <QRCodeCanvas value={vcard} size={172} level="M" fgColor={GREEN} />
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-4 overflow-x-auto">
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
      <Button onClick={copy} variant="outline" data-testid="copy-signature-btn">
        <Icons.Copy className="w-4 h-4 mr-1.5" /> Copy signature
      </Button>
    </div>
  );
}
